import { create } from 'zustand'
import { RemoteConfigSchema, RemoteConfigSchemaType } from './remoteConfigSchema'
import { persist, createJSONStorage, subscribeWithSelector } from 'zustand/middleware'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { Platform } from 'react-native'
import { getAnalytics, setUserId, setUserProperties } from '@react-native-firebase/analytics'
import { getRemoteConfig, setCustomSignals } from '@react-native-firebase/remote-config'
import { firebaseApp } from '../firebase'
import { version } from '../../../package.json'
import { getExpoConfig } from '../../app/hooks/useConfig'

const defaultRemoteConfig = RemoteConfigSchema.parse({})

const {
  version: appVersion,
  extra: { channel, branch } = {}
} = getExpoConfig()
console.log('APP CONFIG', { appVersion, channel, branch })

export const useRemoteConfigStatus = create<{ isInitialized: boolean }>(() => ({ isInitialized: false }))

export const useRemoteConfig = create(
  subscribeWithSelector(
    persist(() => defaultRemoteConfig, {
      name: 'remote-config',
      version: 1,
      storage: createJSONStorage(() => AsyncStorage),
      merge: (persistedState, currentState) => {
        const parsedPersistedState = RemoteConfigSchema.safeParse(persistedState)
        if (parsedPersistedState.success) {
          return parsedPersistedState.data
        }
        return currentState
      }
    })
  )
)

// Sample usage:
// const foo = useRemoteConfig<RemoteConfigSchemaType["test_json"]>((state) => state.test_json)
// const bar = useRemoteConfig((state) => state.test_json)

export type RemoteConfigUserContext = {
  analyticsUserProperties: {
    id: string
    user_type: string
    user_privileges: string
    email: string
    phoneNumber?: string
    createdAt: string
    platform: string
    stage: string
    appVersion: string
    channel: string
    branch: string
  } | null
}

// Firebase web doesn't share user properties between analytics and remote config so we leverage our patch to firebase forcing the remote config request to include the user properties
export const useRemoteConfigUserContext = create(
  persist<RemoteConfigUserContext | undefined>(() => undefined, {
    name: 'remote-config-user-context',
    version: 1
  })
)
type AnalyticProperties = NonNullable<RemoteConfigUserContext['analyticsUserProperties']>
type AnalyticPropertiesWithoutOptionals = Omit<AnalyticProperties, 'appVersion' | 'channel' | 'branch'>
type UserContextPayload = Omit<RemoteConfigUserContext, 'analyticsUserProperties'> & {
  analyticsUserProperties: AnalyticPropertiesWithoutOptionals
}

// Remote Config's `id` remains a Mongo targeting property; Firebase's user ID is Auth0.
export async function setRemoteConfigUserContext(userContext: UserContextPayload, auth0Id: string | null | undefined) {
  const appVersion = version
  const expoConfig = await getExpoConfig()
  const channel = expoConfig?.extra?.channel ?? 'missing'
  const branch = expoConfig?.extra?.branch ?? 'missing'

  if (Platform.OS === 'web') {
    const userContextWithAnalyticsProperties: RemoteConfigUserContext = {
      ...userContext,
      analyticsUserProperties: userContext.analyticsUserProperties
        ? {
            ...userContext.analyticsUserProperties,
            appVersion,
            channel,
            branch
          }
        : null
    }
    console.log('SET REMOTE CONFIG > web', userContextWithAnalyticsProperties)
    useRemoteConfigUserContext.setState(userContextWithAnalyticsProperties)
  }
  if (!userContext.analyticsUserProperties) {
    console.log('SET REMOTE CONFIG > empty', userContext.analyticsUserProperties)
    return
  }
  const { id: targetingUserId, ...rest } = userContext.analyticsUserProperties

  const analytics = getAnalytics(firebaseApp())
  const remoteConfig = getRemoteConfig(firebaseApp())
  await setCustomSignals(remoteConfig, { id: targetingUserId, ...rest, appVersion, channel, branch })
  if (auth0Id?.trim()) {
    await setUserId(analytics, auth0Id)
  }
  await setUserProperties(analytics, userContext.analyticsUserProperties)
}
